import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Boxes, Plus, Trash2 } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import { SELECT_CLASS } from '../components/AudiencePicker';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { customObjectErrorText, useCustomObjectTypes } from '../lib/useCustomObjects';
import type { CustomFieldType, CustomObjectType } from '../types/api';

const FIELD_TYPE_LABEL: Record<CustomFieldType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  boolean: 'Yes / no',
  select: 'Choice',
};
const FIELD_TYPES = Object.keys(FIELD_TYPE_LABEL) as CustomFieldType[];
const MAX_TYPES = 10;
const MAX_FIELDS = 20;

/** A field row in the editor. `saved` fields keep their key (records use it). */
interface FieldDraft {
  rowId: number;
  key: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  options: string;
  saved: boolean;
}

interface TypeDraft {
  id: string | null;
  name: string;
  slug: string;
  slugTouched: boolean;
  fields: FieldDraft[];
  recordCount: number;
}

let rowSeq = 0;

/** "Pet's birthday" → "pets_birthday"; starts with a letter, or is empty. */
function toKey(label: string, sep: '_' | '-'): string {
  const base = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .replace(/[^a-z0-9]+/g, sep)
    .replace(/^[^a-z]+/, '')
    .slice(0, 40);
  return base.endsWith(sep) ? base.slice(0, -1) : base;
}

function draftFrom(type: CustomObjectType | null): TypeDraft {
  if (!type) {
    return { id: null, name: '', slug: '', slugTouched: false, fields: [], recordCount: 0 };
  }
  return {
    id: type.id,
    name: type.name,
    slug: type.slug,
    slugTouched: true,
    recordCount: type.recordCount ?? 0,
    fields: type.fields.map((f) => ({
      rowId: ++rowSeq,
      key: f.key,
      label: f.label,
      type: f.type,
      required: f.required,
      options: (f.options ?? []).join(', '),
      saved: true,
    })),
  };
}

function uniqueKey(label: string, taken: Set<string>): string {
  const base = toKey(label, '_') || 'field';
  let key = base;
  for (let i = 2; taken.has(key); i++) key = `${base.slice(0, 37)}_${i}`;
  return key;
}

function payload(d: TypeDraft) {
  const taken = new Set(d.fields.filter((f) => f.saved).map((f) => f.key));
  return {
    name: d.name.trim(),
    slug: d.slug.trim(),
    fields: d.fields.map((f) => {
      let key = f.key;
      if (!f.saved) {
        key = uniqueKey(f.label, taken);
        taken.add(key);
      }
      const options = f.options
        .split(',')
        .map((o) => o.trim())
        .filter((o) => o !== '');
      return {
        key,
        label: f.label.trim(),
        type: f.type,
        required: f.required,
        ...(f.type === 'select' ? { options } : {}),
      };
    }),
  };
}

function TypeEditor({
  initial,
  onCancel,
  onSaved,
}: {
  initial: TypeDraft;
  onCancel: () => void;
  onSaved: (type: CustomObjectType) => void;
}) {
  const [d, setD] = useState(initial);
  const save = useMutation({
    mutationFn: () =>
      d.id
        ? apiFetch<CustomObjectType>(`/custom-objects/types/${d.id}`, {
            method: 'PUT',
            body: payload(d),
          })
        : apiFetch<CustomObjectType>('/custom-objects/types', { method: 'POST', body: payload(d) }),
    onSuccess: onSaved,
  });
  const setField = (rowId: number, patch: Partial<FieldDraft>) =>
    setD((cur) => ({
      ...cur,
      fields: cur.fields.map((f) => (f.rowId === rowId ? { ...f, ...patch } : f)),
    }));
  const locked = d.recordCount > 0;
  const error = customObjectErrorText(save.error, 'The object type could not be saved.');
  const canSave =
    d.name.trim() !== '' && d.slug.trim() !== '' && d.fields.every((f) => f.label.trim() !== '');

  return (
    <Card className="p-4 flex flex-col gap-4" data-testid="custom-object-editor">
      <h2 className="text-sm font-semibold m-0">
        {d.id ? `Edit ${initial.name}` : 'New object type'}
      </h2>
      <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="co-name">Name</Label>
          <Input
            id="co-name"
            maxLength={60}
            placeholder="Treatment"
            value={d.name}
            onChange={(e) => {
              const name = e.target.value;
              setD((cur) => ({
                ...cur,
                name,
                slug: cur.slugTouched ? cur.slug : toKey(name, '-'),
              }));
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="co-slug">Slug</Label>
          <Input
            id="co-slug"
            maxLength={40}
            placeholder="treatment"
            value={d.slug}
            onChange={(e) => setD((cur) => ({ ...cur, slug: e.target.value, slugTouched: true }))}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="text-sm font-medium">Fields</div>
        {locked ? (
          <p className="text-muted text-xs m-0">
            This type has {d.recordCount} {d.recordCount === 1 ? 'record' : 'records'}, so the type
            of a saved field cannot change. You can rename or remove fields, and add new ones.
            Values of a removed field are hidden.
          </p>
        ) : null}
        {d.fields.length === 0 ? <p className="text-muted text-sm m-0">No fields yet.</p> : null}
        <ul className="m-0 p-0 list-none flex flex-col gap-2">
          {d.fields.map((f, i) => (
            <li
              key={f.rowId}
              className="border border-border-soft rounded-[10px] p-3 flex flex-col gap-2"
              data-testid="custom-field-row"
            >
              <div className="flex gap-2 items-end flex-wrap">
                <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
                  <Label htmlFor={`co-field-label-${f.rowId}`}>Label</Label>
                  <Input
                    id={`co-field-label-${f.rowId}`}
                    maxLength={60}
                    value={f.label}
                    onChange={(e) => setField(f.rowId, { label: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1 w-[130px] max-sm:flex-1">
                  <Label htmlFor={`co-field-type-${f.rowId}`}>Type</Label>
                  <select
                    id={`co-field-type-${f.rowId}`}
                    className={SELECT_CLASS}
                    value={f.type}
                    disabled={locked && f.saved}
                    onChange={(e) => setField(f.rowId, { type: e.target.value as CustomFieldType })}
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {FIELD_TYPE_LABEL[t]}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Remove field ${f.label || i + 1}`}
                  onClick={() =>
                    setD((cur) => ({
                      ...cur,
                      fields: cur.fields.filter((x) => x.rowId !== f.rowId),
                    }))
                  }
                >
                  <Trash2 size={16} />
                </Button>
              </div>
              {f.type === 'select' ? (
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`co-field-options-${f.rowId}`}>
                    Options, separated by commas
                  </Label>
                  <Input
                    id={`co-field-options-${f.rowId}`}
                    placeholder="small, medium, large"
                    value={f.options}
                    onChange={(e) => setField(f.rowId, { options: e.target.value })}
                  />
                </div>
              ) : null}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={f.required}
                  onChange={(e) => setField(f.rowId, { required: e.target.checked })}
                />
                Required
                {f.saved ? <span className="text-muted text-xs ml-auto">key: {f.key}</span> : null}
              </label>
            </li>
          ))}
        </ul>
        <div>
          <Button
            size="sm"
            variant="secondary"
            disabled={d.fields.length >= MAX_FIELDS}
            onClick={() =>
              setD((cur) => ({
                ...cur,
                fields: [
                  ...cur.fields,
                  {
                    rowId: ++rowSeq,
                    key: '',
                    label: '',
                    type: 'text',
                    required: false,
                    options: '',
                    saved: false,
                  },
                ],
              }))
            }
          >
            <Plus size={16} /> Add field
          </Button>
          {d.fields.length >= MAX_FIELDS ? (
            <span className="text-muted text-xs ml-2">At most {MAX_FIELDS} fields.</span>
          ) : null}
        </div>
      </div>

      {error ? <Alert>{error}</Alert> : null}
      <div className="flex gap-2">
        <Button size="sm" disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
          {d.id ? 'Save' : 'Create type'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

/**
 * Settings, Custom objects (K8). A company's own record kinds, e.g. a
 * treatment or a pet, each with its own fields. Records are added on the
 * contact page. Nothing else uses them yet.
 */
export function CustomObjectsPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { types, isLoading, isError } = useCustomObjectTypes();
  const [editing, setEditing] = useState<TypeDraft | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['custom-objects'] });

  const remove = useMutation({
    mutationFn: (type: CustomObjectType) =>
      apiFetch<{ result: string; records: number }>(`/custom-objects/types/${type.id}`, {
        method: 'DELETE',
      }),
    onSuccess: (res, type) => {
      setNotice(
        `"${type.name}" has been deleted${res.records > 0 ? ` with ${res.records} ${res.records === 1 ? 'record' : 'records'}` : ''}.`,
      );
      invalidate();
    },
  });

  const deleteError = customObjectErrorText(remove.error, 'The object type could not be deleted.');

  return (
    <>
      <PageHeader
        title="Custom objects"
        badge={<MockBadge />}
        description="Your own kinds of records, each linked to a contact, for example a treatment with an area, a date and a size. Define the fields here; add records on a contact's page."
      />
      {!isAdmin ? (
        <p className="text-muted text-sm m-0">Only admins can change object types.</p>
      ) : null}
      {notice ? (
        <div role="status" className="text-success-text text-sm">
          {notice}
        </div>
      ) : null}
      {deleteError ? <Alert>{deleteError}</Alert> : null}
      {isLoading ? <div className="text-muted">Loading object types…</div> : null}
      {isError ? <Alert>The object types could not be loaded.</Alert> : null}

      {editing ? (
        <TypeEditor
          key={editing.id ?? 'new'}
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={(type) => {
            setEditing(null);
            setNotice(`"${type.name}" has been saved.`);
            invalidate();
          }}
        />
      ) : isAdmin && !isLoading && !isError ? (
        <div>
          <Button
            size="sm"
            disabled={types.length >= MAX_TYPES}
            onClick={() => {
              setNotice(null);
              setEditing(draftFrom(null));
            }}
          >
            <Plus size={16} /> New object type
          </Button>
          {types.length >= MAX_TYPES ? (
            <span className="text-muted text-xs ml-2">At most {MAX_TYPES} object types.</span>
          ) : null}
        </div>
      ) : null}

      {!isLoading && !isError && types.length === 0 && !editing ? (
        <EmptyState
          icon={<Boxes />}
          title="No object types yet"
          description="Create one to keep structured records on your contacts, such as treatments, appointments or pets."
        />
      ) : null}

      <ul className="flex flex-col gap-3 list-none p-0 m-0">
        {types.map((type) => (
          <li key={type.id}>
            <Card className="p-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="font-medium">{type.name}</div>
                  <div className="text-muted text-sm">
                    {type.fields.length} {type.fields.length === 1 ? 'field' : 'fields'} ·{' '}
                    {type.recordCount ?? 0} {(type.recordCount ?? 0) === 1 ? 'record' : 'records'}
                  </div>
                  {type.fields.length > 0 ? (
                    <div className="text-muted text-xs mt-1 break-words">
                      {type.fields
                        .map((f) => `${f.label} (${FIELD_TYPE_LABEL[f.type]})`)
                        .join(', ')}
                    </div>
                  ) : null}
                </div>
                {isAdmin ? (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setNotice(null);
                        setEditing(draftFrom(type));
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      aria-label={`Delete ${type.name}`}
                      disabled={remove.isPending}
                      onClick={() => {
                        const n = type.recordCount ?? 0;
                        const ok =
                          n === 0 ||
                          window.confirm(
                            `Delete "${type.name}" and its ${n} ${n === 1 ? 'record' : 'records'}? This cannot be undone.`,
                          );
                        if (!ok) return;
                        setNotice(null);
                        remove.mutate(type);
                      }}
                    >
                      Delete
                    </Button>
                  </>
                ) : null}
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </>
  );
}
